import { useState, useEffect } from "react";
import Modal from "@/components/common/Modal";
import { useApp } from "@/contexts/AppContext";
import { AnalyticsFiltersDrawer } from "@/features/analytics/components/AnalyticsFiltersDrawer";
import {
  cloneAnalyticsFilters,
  createEmptyAnalyticsFilters,
  countActiveAnalyticsFilters,
} from "@/features/analytics/utils/analytics";

const filtersToPredefinedFilter = (filters) => {
  const payload = {};
  for (const key of Object.keys(filters)) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      payload[key] = [...value];
    }
  }
  return payload;
};

const predefinedFilterToAnalyticsFilters = (predefinedFilter) => {
  const base = createEmptyAnalyticsFilters();
  if (!predefinedFilter || typeof predefinedFilter !== "object") return base;
  for (const key of Object.keys(base)) {
    const value = predefinedFilter[key];
    if (Array.isArray(value)) {
      base[key] = [...value];
    }
  }
  return base;
};

export default function EditUserModal({ isOpen, onClose, user }) {
  const { updateUser, campaigns, availableChannels, availableCategories } =
    useApp();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [predefinedFilters, setPredefinedFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [draftFilters, setDraftFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setRole(user.role || "user");
      setPredefinedFilters(
        predefinedFilterToAnalyticsFilters(user.predefinedFilter),
      );
      setPassword("");
    }
  }, [user]);

  const resetForm = () => {
    if (user) {
      setUsername(user.username || "");
      setRole(user.role || "user");
      setPredefinedFilters(
        predefinedFilterToAnalyticsFilters(user.predefinedFilter),
      );
    } else {
      setUsername("");
      setRole("user");
      setPredefinedFilters(createEmptyAnalyticsFilters());
    }
    setPassword("");
    setError("");
  };

  const handleSubmit = async () => {
    if (!username) {
      setError("Username is required");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const updates = {};
      if (username !== user.username) {
        updates.username = username;
      }
      if (password) {
        updates.password = password;
      }
      if (role !== user.role) {
        updates.role = role;
      }
      // Only send predefinedFilter if the user is (or will become) a provider —
      // for other roles it's irrelevant and only adds noise to the payload.
      if (role === "provider") {
        updates.predefinedFilter = filtersToPredefinedFilter(predefinedFilters);
      } else if (user.predefinedFilter) {
        // Clearing scope when role moves away from provider.
        updates.predefinedFilter = null;
      }

      await updateUser(user.uuid, updates);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const activeScopeCount = countActiveAnalyticsFilters(predefinedFilters);

  const footer = (
    <>
      <button
        className="btn btn-secondary"
        type="button"
        onClick={() => {
          resetForm();
          onClose();
        }}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        className="btn btn-primary"
        type="button"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Updating..." : "Update User"}
      </button>
    </>
  );

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title="Edit User"
      footer={footer}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}
      <div className="form-group-modal">
        <label htmlFor="edit-username">Username *</label>
        <input
          id="edit-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter username"
          autoComplete="username"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="edit-password">
          New Password (leave blank to keep current)
        </label>
        <input
          id="edit-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter new password"
          autoComplete="new-password"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="edit-role">Role *</label>
        <select
          id="edit-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="provider">Provider</option>
        </select>
      </div>
      {role === "provider" ? (
        <div className="form-group-modal">
          <label>Predefined scope (provider sees only matching data)</label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setDraftFilters(cloneAnalyticsFilters(predefinedFilters));
                setIsFiltersOpen(true);
              }}
              disabled={loading}
            >
              Edit scope
            </button>
            <span
              style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}
            >
              {activeScopeCount > 0
                ? `${activeScopeCount} filter group(s) set`
                : "No scope set — provider will see nothing"}
            </span>
          </div>
        </div>
      ) : null}

      <AnalyticsFiltersDrawer
        isOpen={isFiltersOpen}
        value={draftFilters}
        campaigns={campaigns || []}
        channels={availableChannels || []}
        categories={availableCategories || []}
        onChange={setDraftFilters}
        onClose={() => setIsFiltersOpen(false)}
        onApply={() => {
          setPredefinedFilters(cloneAnalyticsFilters(draftFilters));
          setIsFiltersOpen(false);
        }}
        onReset={() => setDraftFilters(createEmptyAnalyticsFilters())}
      />
    </Modal>
  );
}
